import { ReactNode, useEffect } from 'react'
import { GetServerSideProps, NextPage } from 'next'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'

import EmptySearchResult from '@/components/EmptySearchResult'
import Footer from '@/components/Footer'
import Header from '@/components/Header'
import HomeBanner from '@/components/HomeBanner'
import SearchResultsComponent, {
  EmptySearchResultVariant,
} from '@/components/SearchResultsComponent'
import { HeaderVariant, OrderMethod } from '@/libs/helpers/utils'
import { useGoogleMaps } from '@/providers/googleMaps'
import { SearchResults, useSearch } from '@/providers/state/search'
import { CircularLoader, Grid } from '@/tbui'

interface SearchResultsPageProps {
  orderMethod: OrderMethod
}

export const getServerSideProps: GetServerSideProps<SearchResultsPageProps> = async (context) => {
  return { props: { orderMethod: OrderMethod[context.params?.orderMethod ?? 'pickup'] } }
}

const SearchResultsPage: NextPage<SearchResultsPageProps> = ({ orderMethod }) => {
  const [
    { searchResultsFar, searchResultsNear, orderingVenue },
    { getSearchResultNear, getSearchResultFar, clearSearchResults },
  ] = useSearch()
  const [{ orderingAddress }] = useGoogleMaps()
  const { t } = useTranslation()
  const router = useRouter()

  useEffect(() => {
    const shouldGetResults = ({
      state,
      getResults,
    }: {
      state?: SearchResults | null
      getResults: (...args: unknown[]) => Promise<void> | void
    }): void => {
      if (orderingAddress && !orderingVenue) {
        const params = {
          orderMethod,
          lat: orderingAddress.lat,
          lng: orderingAddress.lng,
        }
        if (!state) {
          getResults(params)
        }
      }
    }

    shouldGetResults({
      getResults: getSearchResultFar,
      state: searchResultsFar,
    })
    shouldGetResults({
      getResults: getSearchResultNear,
      state: searchResultsNear,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResultsNear, searchResultsFar, orderingAddress, orderingVenue, orderMethod])

  useEffect(() => {
    if (!orderingAddress) {
      router.push('/')
    }

    return () => {
      clearSearchResults()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderingAddress])

  const renderResults = (): ReactNode => {
    if (!searchResultsFar || !searchResultsNear) {
      return (
        <Grid justify="center" p="0 24px" m="100px 0" template="auto">
          <CircularLoader fontSize="60px" />
          <p>{t('loading')}</p>
        </Grid>
      )
    }

    if (!searchResultsFar.data.length && !searchResultsNear.data.length) {
      return <EmptySearchResult variant={EmptySearchResultVariant.RESTAURANT_AROUND} />
    }

    return (
      <>
        <SearchResultsComponent
          title={t('tbdine.search_results.title', { context: 'near' })}
          results={searchResultsNear.data}
          itemsPerPage={12}
          orderMethod={orderMethod}
        />
        <SearchResultsComponent
          title={t('tbdine.search_results.title', {
            city: orderingAddress?.city,
            context: 'far',
          })}
          results={searchResultsFar.data}
          itemsPerPage={12}
          orderMethod={orderMethod}
        />
      </>
    )
  }

  return (
    <>
      <Header
        variant={HeaderVariant.SEARCH}
        cart={{ isCartLoading: false, orderMethod }}
        venueXRefID={undefined}
      />
      <HomeBanner orderMethod={orderMethod} />
      {renderResults()}
      <Footer />
    </>
  )
}

export default SearchResultsPage
